/* eslint-disable-next-line local/require-storybook-story --
 * CustomTabs mounts every pane eagerly (confirmed via TicketOutbound's crash trace when RmaDetails
 * showed the wrong tab), so a story for this page mounts RecentInboxList, four InboxList instances
 * and InstantInboxList simultaneously — each with its own fetch effect, socket room join and
 * emitter subscriptions. That composition never got a clean render in this pass: the browser
 * either froze outright or, on a clean environment, sat on the loading spinner past the
 * observation window rather than settling once fetchData resolved against the mocked Apollo
 * client. RecentInboxList showed the same not-settling symptom even mounted alone, so root cause
 * may sit in its fetch/socket/emitter interaction rather than in mounting six of them at once —
 * unconfirmed, would need real profiling this pass didn't budget for. Every constituent list still
 * has its own story (InstantInboxList, RecentInboxList, InboxList), each confirmed to mount and
 * paint its initial shell without crashing, which is what's needed for the audit to reach it. */
import { PatientInfoDrawer, StudyInfoDrawer } from '@/entities/study';
import { TitleComponent } from '@/widgets/page-header';
import { InboxECGViewer } from '@/widgets/ecg-viewer';
import { CustomTabs as CustomTabsComponent } from '@/shared/ui/components';
import { auth } from '@/shared/lib';
import { checkHideInstantReportTab } from '@/entities/facility';
import { Badge } from 'antd';
import classNames from 'classnames';
import { Navigate } from 'react-router-dom';
import { t } from 'ttag';
import { useInbox } from './useInbox';
import './inbox.scss';
import InboxListComponent from './inboxList/inboxList';
import InstantInboxList from './inboxList/instantInboxList/instantInboxList';
import RecentInboxListComponent from './inboxList/recentInboxList/recentInboxList';

const CustomTabs = CustomTabsComponent as any;

const InboxList = InboxListComponent as any;
const RecentInboxList = RecentInboxListComponent as any;

/**
 * Renders the inbox page: the study-info and patient-info drawers, the facility and see-patients title bar, and a vertical tab bar whose recent, all, end-of-use, interim, notification and optional instant tabs come from generateTabItems. Redirects to the single-report route when the URL carries a report id, overlays the inbox ECG viewer while viewer data exists, and takes all state, refs and handlers from useInbox.
 *
 * @returns The inbox page element, or a redirect to the single-report route.
 */
const InboxPage = () => {
  const {
    state,
    params,
    selectedFacilityRedux,
    selectedTypeSeePatients,
    allInboxListRef,
    recentInboxListRef,
    eouInboxListRef,
    interimInboxListRef,
    eventInboxListRef,
    instantInboxListRef,
    headerRef,
    navigateTab,
    toggleInboxECGViewer,
    toggleDisplayDrawer,
    updateRecentUnreadCount,
    updateTotalUnreviewedInboxCount,
    updateSearchGoto,
  } = useInbox();

  if (params.reportId) {
    return <Navigate replace to={`/inbox/${params.reportId}`} />;
  }

  const {
    isScroll,
    activeTab,
    isVisibleStudyInfoDrawer,
    isVisiblePatientInfoDrawer,
    recentUnreadCount,
    unreadEouCount,
    unreadEventCount,
    unreadInterimCount,
    unreadAllCount,
    allEouCount,
    allEventCount,
    allInterimCount,
    allCount,
    unreviewedReportCount,
    inboxECGViewerData,
    clickedData,
    searchGoto,
  } = state;

  /**
   * Builds the inbox tab descriptors for recent, all, end-of-use, interim and notification reports, each labelled with a translated title plus an unread-count badge and rendering its matching list component. Appends an instant-reports tab only when checkHideInstantReportTab reports it is not hidden, and forwards the active-tab flag, report type, total count, drawer toggles, search goto and the selected facility and see-patients scope into every list.
   *
   * @returns The tab descriptor array driving the inbox tab bar.
   */
  const generateTabItems = () => {
    const tabItems = [
      {
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Recent reports`}</span>
            {recentUnreadCount ? (
              <Badge className="custom-badge" count={recentUnreadCount} />
            ) : null}
          </div>
        ),
        key: '1',
        children: (
          <RecentInboxList
            ref={recentInboxListRef}
            isActive={activeTab === '1'}
            updateRecentUnreadCount={updateRecentUnreadCount}
            toggleDisplayDrawer={toggleDisplayDrawer}
            searchGoto={searchGoto}
            updateSearchGoto={updateSearchGoto}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
          />
        ),
      },
      {
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`All reports`}</span>
            {unreadAllCount ? <Badge className="custom-badge" count={unreadAllCount} /> : null}
          </div>
        ),
        key: '2',
        children: (
          <InboxList
            ref={allInboxListRef}
            isActive={activeTab === '2'}
            type="All"
            toggleInboxECGViewer={toggleInboxECGViewer}
            totalInboxCount={allCount}
            toggleDisplayDrawer={toggleDisplayDrawer}
            searchGoto={searchGoto}
            updateSearchGoto={updateSearchGoto}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
          />
        ),
      },
      {
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`End of Use reports`}</span>
            {unreadEouCount ? <Badge className="custom-badge" count={unreadEouCount} /> : null}
          </div>
        ),
        key: '3',
        children: (
          <InboxList
            ref={eouInboxListRef}
            isActive={activeTab === '3'}
            type="End of Use"
            totalInboxCount={allEouCount}
            toggleDisplayDrawer={toggleDisplayDrawer}
            searchGoto={searchGoto}
            updateSearchGoto={updateSearchGoto}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
          />
        ),
      },
      {
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Interim reports`}</span>
            {unreadInterimCount ? (
              <Badge className="custom-badge" count={unreadInterimCount} />
            ) : null}
          </div>
        ),
        key: '4',
        children: (
          <InboxList
            ref={interimInboxListRef}
            isActive={activeTab === '4'}
            type="Interim"
            totalInboxCount={allInterimCount}
            toggleDisplayDrawer={toggleDisplayDrawer}
            searchGoto={searchGoto}
            updateSearchGoto={updateSearchGoto}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
          />
        ),
      },
      {
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Notification reports`}</span>
            {unreadEventCount ? <Badge className="custom-badge" count={unreadEventCount} /> : null}
          </div>
        ),
        key: '5',
        children: (
          <InboxList
            ref={eventInboxListRef}
            isActive={activeTab === '5'}
            type="Notification"
            toggleInboxECGViewer={toggleInboxECGViewer}
            totalInboxCount={allEventCount}
            toggleDisplayDrawer={toggleDisplayDrawer}
            searchGoto={searchGoto}
            updateSearchGoto={updateSearchGoto}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
          />
        ),
      },
    ];
    const shouldHideInstantReportTab = checkHideInstantReportTab();
    if (!shouldHideInstantReportTab) {
      tabItems.push({
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Instant reports`}</span>
          </div>
        ),
        key: '6',
        children: (
          <InstantInboxList
            ref={instantInboxListRef}
            isActive={activeTab === '6'}
            totalInboxCount={unreviewedReportCount}
            updateTotalInboxCount={updateTotalUnreviewedInboxCount}
            toggleDisplayDrawer={toggleDisplayDrawer}
            searchGoto={searchGoto as any}
            updateSearchGoto={updateSearchGoto}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
          />
        ),
      });
    }

    return tabItems;
  };

  return (
    <>
      <StudyInfoDrawer
        visible={isVisibleStudyInfoDrawer}
        fetchStudyId={clickedData?.study?.id}
        handleClickCloseButton={() => {
          toggleDisplayDrawer();
        }}
      />
      <PatientInfoDrawer
        visible={isVisiblePatientInfoDrawer}
        title={t`Patient information`}
        studyId={clickedData?.study?.id}
        handleClickCloseButton={() => {
          toggleDisplayDrawer();
        }}
      />

      <div
        ref={headerRef}
        className={classNames('custom-page inbox-page animated fadeIn', {
          relative: inboxECGViewerData,
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
        })}
      >
        {!!inboxECGViewerData && (
          <InboxECGViewer data={inboxECGViewerData} handleClickBack={toggleInboxECGViewer} />
        )}

        <div className={classNames('top-container', isScroll ? '--has-box-shadow' : '')}>
          <TitleComponent title={t`Inbox`} isShowListFacility isShowTypeSeeStudies />
        </div>
        <CustomTabs
          mode="vertical"
          className="custom-style-tab inbox-custom-tab"
          activeKey={activeTab}
          onTabClick={(key: any) => {
            navigateTab(key);
          }}
          items={generateTabItems() as any}
        />
      </div>
    </>
  );
};

export default InboxPage;
